/**
 * Owner-uploaded branding: light/dark logo assets served from the backend.
 *
 *  - `loadBrandingMeta()` fetches (once, memoised) which variants are uploaded
 *    and their versions. Public endpoint, works pre-login.
 *  - `subscribeBranding(fn)` lets components refresh when the owner uploads,
 *    removes, or on a page load reload.
 *  - `invalidateBranding()` re-fetches and notifies subscribers — call this
 *    from the upload/remove buttons in Settings so headers update instantly.
 *  - `brandingLogoUrl(variant, version)` builds a cache-bust URL for `<img>`.
 */

const API = process.env.REACT_APP_BACKEND_URL;

const state = { meta: null, promise: null, listeners: new Set() };

function notify() {
  for (const l of state.listeners) {
    try { l(state.meta); } catch { /* subscriber error is not fatal */ }
  }
}

export function subscribeBranding(fn) {
  state.listeners.add(fn);
  // Fire immediately with current state so subscribers hydrate.
  try { fn(state.meta); } catch { /* noop */ }
  return () => { state.listeners.delete(fn); };
}

export function currentBrandingMeta() {
  return state.meta;
}

export function loadBrandingMeta(force = false) {
  if (!force && state.meta) return Promise.resolve(state.meta);
  if (state.promise) return state.promise;
  state.promise = fetch(`${API}/api/branding/meta`, { cache: "no-store" })
    .then((r) => (r.ok ? r.json() : { light: null, dark: null }))
    .catch(() => ({ light: null, dark: null }))
    .then((m) => {
      state.meta = m;
      state.promise = null;
      notify();
      return m;
    });
  return state.promise;
}

export async function invalidateBranding() {
  state.meta = null;
  state.promise = null;
  return loadBrandingMeta(true);
}

export function brandingLogoUrl(variant, version) {
  const v = version || 1;
  return `${API}/api/branding/logo?variant=${variant}&v=${v}`;
}

export function brandingFaviconUrl(size, version) {
  const v = version || 1;
  return `${API}/api/branding/favicon?size=${size}&v=${v}`;
}

export function brandingManifestUrl(version) {
  const v = version || 1;
  return `${API}/api/branding/manifest.webmanifest?v=${v}`;
}

/**
 * Fetch the uploaded logo (light variant) as a base64 data URL, for embedding
 * into exported SVGs. Returns { dataUrl, mime, width, height } or null.
 *
 * Fail-open: on ANY failure (fetch error, non-2xx, timeout, decode failure,
 * missing manifest) return null and let the caller export WITHOUT a logo
 * rather than aborting the entire export. Hard 3-second cap on network
 * fetches — on a custom production domain a slow CDN or a mis-configured
 * CORS response would otherwise hang the raster path indefinitely (users
 * see "PNG failed" with no download).
 */
export async function fetchBrandingLogoAsDataURL(variant = "light") {
  const withTimeout = (p, ms, label) => {
    let t;
    const timeout = new Promise((_, rej) => {
      t = setTimeout(() => rej(new Error(`${label} timed out after ${ms}ms`)), ms);
    });
    return Promise.race([p, timeout]).finally(() => clearTimeout(t));
  };
  try {
    const meta = await withTimeout(loadBrandingMeta(), 3000, "branding meta").catch((e) => {
      console.warn("[export] branding meta unavailable:", e && e.message ? e.message : e);
      return null;
    });
    const info = meta && meta[variant];
    if (!info) return null;
    const res = await withTimeout(
      fetch(brandingLogoUrl(variant, info.version), { cache: "no-store" }),
      3000,
      "branding logo fetch"
    );
    if (!res || !res.ok) {
      console.warn("[export] branding logo fetch not OK:", res && res.status);
      return null;
    }
    const blob = await withTimeout(res.blob(), 3000, "branding logo blob");
    const dataUrl = await new Promise((resolve, reject) => {
      const r = new FileReader();
      r.onload = () => resolve(r.result);
      r.onerror = reject;
      r.readAsDataURL(blob);
    });
    // Measure natural dimensions
    const dims = await new Promise((resolve) => {
      if (info.mime === "image/svg+xml") {
        blob.text().then((txt) => {
          const m = txt.match(/viewBox=["']([^"']+)["']/i);
          if (m) {
            const parts = m[1].split(/[\s,]+/).map(parseFloat);
            if (parts.length === 4) {
              resolve({ width: parts[2], height: parts[3] });
              return;
            }
          }
          resolve({ width: 240, height: 60 });
        }).catch(() => resolve({ width: 240, height: 60 }));
        return;
      }
      const img = new Image();
      img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight });
      img.onerror = () => resolve({ width: 240, height: 60 });
      img.src = dataUrl;
    });
    return { dataUrl, mime: info.mime, ...dims };
  } catch (e) {
    console.warn("[export] branding logo embed failed, exporting without logo:", e && e.message ? e.message : e);
    return null;
  }
}
